/**
 * Executive Reconciliation Session
 *
 * The demo climax. Executives see all proposals from downstream groups
 * side by side, choose a reconciliation mode, and produce a final
 * accepted ranking that feeds the Plan of Record.
 *
 * Three modes:
 *   Mode 1 — Accept Team Rankings  (trust downstream, click accept)
 *   Mode 2 — Aggregate Scoring     (statistical combination shown, exec confirms)
 *   Mode 3 — Executive Session     (exec runs their own final ranking — demo focus)
 *
 * Route: /prioritization/cascade/reconcile/:cycleId
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  BarChart3,
  Users,
  GripVertical,
  Loader2,
  Check,
  AlertCircle,
  ArrowRight,
  Trophy,
  ListOrdered,
  Play,
  Lock,
} from 'lucide-react';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useProposalPacketStore } from '../../../stores/prioritization/proposalPacketStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { createSessionWithFeedback } from '../../../lib/sessions/createSessionFlow';
import type { CatchballProposal, ProposalStatus, ReadinessSnapshot } from '../../../types/prioritization/catchballProposal';
import {
  PROPOSAL_AWAITING_DECISION_STATUSES,
  isProposalAwaitingDecision,
} from '../../../types/prioritization/catchballProposal';
import { CatchballStickyHeader } from '../../../components/prioritization/catchball/CatchballStickyHeader';
import {
  useCatchballAcceptRight,
  useCatchballProvisionalAcceptRights,
} from '../../../hooks/prioritization/useActRight';
import { CallBehindProposal } from '../../../components/prioritization/catchball/CallBehindProposal';

// ── Types ─────────────────────────────────────────────────────────────────────

type ReconciliationMode = 'accept' | 'aggregate' | 'session';

interface RankedEntry {
  rank: number;
  title: string;
  rationale: string;
  sourceOrgUnit: string;
  proposalId: string;
  accepted: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const MODE_META: Record<ReconciliationMode, { label: string; icon: React.ReactNode; description: string }> = {
  accept: {
    label: 'Mode 1 — Accept Team Rankings',
    icon: <CheckCircle2 className="h-5 w-5" />,
    description:
      'Trust the downstream synthesis. Accept each group\'s proposal as submitted. Best when you have high confidence in the downstream process.',
  },
  aggregate: {
    label: 'Mode 2 — Aggregate Scoring',
    icon: <BarChart3 className="h-5 w-5" />,
    description:
      'Combine rankings statistically across groups. Items appearing in multiple proposals rise to the top. You confirm or adjust the aggregate result.',
  },
  session: {
    label: 'Mode 3 — Executive Reconciliation Session',
    icon: <Users className="h-5 w-5" />,
    description:
      'Executives review all proposals together and run their own final prioritization session. Produces the strongest decision lineage.',
  },
};

/** Upward packet proposals visible on Executive Reconciliation (includes FY2027 seed). */
const RECONCILIATION_VISIBLE_STATUSES: ProposalStatus[] = [
  ...PROPOSAL_AWAITING_DECISION_STATUSES,
  'accepted',
  'accepted_provisional',
];

function isReconciliationVisibleStatus(status: ProposalStatus): boolean {
  return RECONCILIATION_VISIBLE_STATUSES.includes(status);
}

/** Human-readable status for cards and sidebar (no internal jargon). */
function formatReconciliationProposalStatus(status: ProposalStatus): string {
  switch (status) {
    case 'accepted_provisional':
      return 'Accepted provisional';
    case 'accepted':
      return 'Accepted';
    case 'acknowledged':
      return 'Acknowledged';
    case 'pending':
      return 'Pending';
    case 'countered':
      return 'Returned';
    case 'rejected':
      return 'Rejected';
    case 'withdrawn':
      return 'Withdrawn';
    default:
      return status;
  }
}

/** Infer division/branch short name from org fields or FY2027-style packet rationale. */
function inferBallBranchLabel(proposal: CatchballProposal): string | undefined {
  const org = proposal.proposedByOrgUnitName ?? proposal.proposedByName;
  if (org) {
    const shortened = org.replace(/\s+Division\s*$/i, '').trim();
    return shortened || org;
  }
  const lead = proposal.rationale.trim().match(/^(.+?)\s+Division\b/i);
  return lead ? lead[1].trim() : undefined;
}

function ballHeadingTitle(proposal: CatchballProposal): string {
  const branch = inferBallBranchLabel(proposal);
  if (branch) return `${branch} Ball — Upward Proposal`;
  return `Upward Proposal`;
}

function submittedByLine(proposal: CatchballProposal): string {
  const named = proposal.proposedByOrgUnitName ?? proposal.proposedByName;
  if (named) return `Submitted by ${named}`;
  const branch = inferBallBranchLabel(proposal);
  if (branch) return `Returned via ${branch} Ball · submitted by ${branch} Division`;
  return 'Returned via Catchball · submitting org not recorded';
}

/** Ranked item titles from readiness snapshot and/or packet rationale (demo seed has rationale only). */
function extractPrioritiesFromPacketProposal(proposal: CatchballProposal): { id: string; title: string }[] {
  const snap = proposal.readinessSnapshot as ReadinessSnapshot | undefined;
  if (snap?.items) {
    const raw = snap.items as unknown;
    if (Array.isArray(raw)) {
      return (raw as { itemId?: string; itemTitle?: string; title?: string }[]).map((row, i) => ({
        id: row.itemId ?? `${proposal.id}-${i}`,
        title: (row.itemTitle ?? row.title ?? `Item ${i + 1}`).trim(),
      }));
    }
    return Object.keys(snap.items).map((itemId) => ({ id: itemId, title: itemId }));
  }
  const text = proposal.rationale;
  const re = /\(\d+\)\s*([^—(]+?)(?=\s*—|\s*\(\d+\)|$)/g;
  const out: { id: string; title: string }[] = [];
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    const title = m[1].trim();
    if (title) out.push({ id: `${proposal.id}-parsed-${i++}`, title });
  }
  return out;
}

function countReturnedPriorities(proposal: CatchballProposal): number {
  return extractPrioritiesFromPacketProposal(proposal).length;
}

function sidebarBallLabel(proposal: CatchballProposal): string {
  const branch = inferBallBranchLabel(proposal);
  if (branch) return `${branch} Ball — Proposal returned`;
  return `Proposal returned`;
}

function statusBadgeClass(status: ProposalStatus): string {
  if (status === 'accepted') return 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]';
  if (status === 'accepted_provisional') return 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
  if (status === 'pending' || status === 'acknowledged') return 'bg-[var(--mustard-dim)] text-[var(--mustard)]';
  return 'bg-[var(--hover-tint)] text-[var(--text-sec)]';
}

// ── Proposal summary card ─────────────────────────────────────────────────────

function ProposalCard({
  proposal,
  mode,
  onAccept,
  accepting,
}: {
  proposal: CatchballProposal;
  mode: ReconciliationMode;
  onAccept: (id: string) => void;
  accepting: boolean;
}) {
  const [expanded, setExpanded] = useState(true);
  const isFullyAccepted = proposal.status === 'accepted';
  const isProvisional = proposal.status === 'accepted_provisional';
  const cannotAcceptFurther = isFullyAccepted;
  // D-4 (migration 987): the database's answer to "may I accept this?" — the
  // rule catchball_accept acts on, including the cycle's decision right.
  const acceptRight = useCatchballAcceptRight(mode === 'accept' && !cannotAcceptFurther ? proposal.id : null);
  const priorityItems = extractPrioritiesFromPacketProposal(proposal);
  const nPriorities = priorityItems.length;
  const updatedAt = proposal.updatedAt ?? proposal.proposedAt;

  const borderClass = isFullyAccepted
    ? 'border-[var(--green)]'
    : isProvisional
      ? 'border-[var(--slate)]'
      : 'border-[var(--rule)]';
  const headerBg = isFullyAccepted
    ? 'bg-[var(--green-dim)] border-[var(--green)]'
    : isProvisional
      ? 'bg-[var(--slate-dim)] border-[var(--slate)]'
      : 'bg-[var(--surface)] border-[var(--rule)]';

  return (
    <div className={` border bg-[var(--bg-raised)] overflow-hidden ${borderClass}`}>
      <div
        className={`px-5 py-4 flex items-center justify-between cursor-pointer ${headerBg} border-b`}
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-3">
          {isFullyAccepted ? (
            <CheckCircle2 className="h-5 w-5 text-[var(--green)] shrink-0" />
          ) : isProvisional ? (
            <CheckCircle2 className="h-5 w-5 text-[var(--text-ter)] shrink-0" />
          ) : (
            <div className="h-5 w-5 rounded-full border-2 border-[var(--rule)] shrink-0" />
          )}
          <div>
            <div className="font-semibold text-[var(--text-pri)]">{ballHeadingTitle(proposal)}</div>
            <div className="text-xs text-[var(--text-sec)] mt-0.5">
              {submittedByLine(proposal)}
              {nPriorities > 0 ? ` · ${nPriorities} proposed priorities` : ''}
              {' · '}
              {formatReconciliationProposalStatus(proposal.status)}
            </div>
            <div className="text-xs text-[var(--text-ter)] mt-0.5">
              Last updated {new Date(updatedAt).toLocaleString()}
            </div>
          </div>
        </div>
        {mode === 'accept' && !cannotAcceptFurther && !acceptRight.allowedProvisional && acceptRight.line && (
          <p className="max-w-xs text-right text-xs text-[var(--text-ter)]">{acceptRight.line}</p>
        )}
        {mode === 'accept' && !cannotAcceptFurther && acceptRight.allowedProvisional && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAccept(proposal.id); }}
            disabled={accepting}
            className="inline-flex items-center gap-1.5 bg-[var(--slate)] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[var(--slate)] disabled:opacity-60"
          >
            {accepting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Accept
          </button>
        )}
      </div>

      {expanded && (
        <div className="divide-y divide-[var(--rule)]">
          {mode === 'accept' && !cannotAcceptFurther && (
            <div className="px-5 py-4">
              <CallBehindProposal proposal={proposal} />
            </div>
          )}
          {/* Rationale */}
          {proposal.rationale && (
            <div className="px-5 py-3">
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-1">Rationale</p>
              <p className="text-sm text-[var(--text-sec)]">{proposal.rationale}</p>
            </div>
          )}

          {/* Evidence */}
          {proposal.supportingEvidence && proposal.supportingEvidence.length > 0 && (
            <div className="px-5 py-3">
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">Supporting evidence</p>
              <ul className="space-y-1">
                {proposal.supportingEvidence.map((e, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[var(--text-sec)]">
                    <span className="mt-1.5 h-1 w-1 rounded-full bg-[var(--text-ter)] shrink-0" />
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Items (snapshot and/or rationale — FY2027 seed uses rationale) */}
          {priorityItems.length > 0 && (
            <div className="px-5 py-3">
              <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">Proposed priorities</p>
              <ol className="space-y-1.5">
                {priorityItems.map((item, i) => (
                  <li key={item.id} className="flex items-start gap-2.5 text-sm">
                    <span className="text-xs font-mono text-[var(--text-ter)] mt-0.5 w-4 shrink-0">{i + 1}</span>
                    <span className="text-[var(--text-pri)]">{item.title}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Aggregate view ────────────────────────────────────────────────────────────

function AggregateView({
  proposals,
  onConfirm,
  confirming,
  refusalLine = null,
}: {
  proposals: CatchballProposal[];
  /** Null when the person may not accept these proposals (D-4); `refusalLine` says why. */
  onConfirm: ((ranking: RankedEntry[]) => void) | null;
  confirming: boolean;
  refusalLine?: string | null;
}) {
  // Score items by rank: rank 1 = N points, rank N = 1 point across all proposals
  const scoreMap = new Map<string, { title: string; score: number; count: number; sources: string[] }>();

  for (const proposal of proposals) {
    const items = extractPrioritiesFromPacketProposal(proposal);
    const N = items.length;
    const branch = inferBallBranchLabel(proposal);
    const sourceBall =
      branch ? `${branch} Ball` : proposal.proposedByOrgUnitName ?? proposal.proposedByName ?? 'Downstream group';
    items.forEach((item, i) => {
      const key = item.title.toLowerCase().trim();
      const entry = scoreMap.get(key) ?? { title: item.title, score: 0, count: 0, sources: [] };
      entry.score += N - i;
      entry.count += 1;
      entry.sources.push(sourceBall);
      scoreMap.set(key, entry);
    });
  }

  const ranked = Array.from(scoreMap.entries())
    .sort((a, b) => b[1].score - a[1].score)
    .map(([key, val], i) => ({
      rank: i + 1,
      title: val.title,
      rationale: `Appeared in ${val.count} upward proposal${val.count !== 1 ? 's' : ''} (different Balls). Sources: ${val.sources.join(', ')}.`,
      sourceOrgUnit: val.sources.join(', '),
      proposalId: key,
      accepted: true,
    }));

  return (
    <div className="space-y-4">
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--rule)] bg-[var(--surface)]">
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">Aggregated ranking</h3>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            Items scored by rank across all upward proposals in this Catchball Loop. Items appearing
            in more Balls and ranked higher receive higher scores.
          </p>
        </div>
        <ol className="divide-y divide-[var(--rule)]">
          {ranked.map((entry) => (
            <li key={entry.proposalId} className="px-5 py-3 flex items-start gap-3">
              <span className="text-sm font-mono font-semibold text-[var(--text-ter)] w-6 mt-0.5">{entry.rank}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--text-pri)]">{entry.title}</p>
                <p className="text-xs text-[var(--text-ter)] mt-0.5">{entry.rationale}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {onConfirm ? (
        <button
          type="button"
          onClick={() => onConfirm(ranked)}
          disabled={confirming || ranked.length === 0}
          className="inline-flex items-center gap-2 bg-[var(--slate)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--slate)] disabled:opacity-60"
        >
          {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
          Accept aggregate ranking
        </button>
      ) : refusalLine ? (
        <p className="text-sm text-[var(--text-ter)]">{refusalLine}</p>
      ) : null}
    </div>
  );
}

// ── Executive Session workspace ───────────────────────────────────────────────

// ─── Shared: deduped item pool ────────────────────────────────────────────────

function buildItemPool(proposals: CatchballProposal[]) {
  const seen = new Set<string>();
  const pool: { title: string; sources: string[]; proposalId: string }[] = [];
  for (const proposal of proposals) {
    const items = extractPrioritiesFromPacketProposal(proposal);
    const branch = inferBallBranchLabel(proposal);
    const ballLabel =
      branch ? `${branch} Ball` : proposal.proposedByOrgUnitName ?? proposal.proposedByName ?? 'Downstream group';
    for (const item of items) {
      const key = item.title.toLowerCase().trim();
      if (seen.has(key)) {
        const existing = pool.find((p) => p.title.toLowerCase() === key);
        if (existing && !existing.sources.includes(ballLabel)) existing.sources.push(ballLabel);
      } else {
        seen.add(key);
        pool.push({ title: item.title, sources: [ballLabel], proposalId: proposal.id });
      }
    }
  }
  return pool;
}

// ─── Sub-view A: Quick drag-and-drop reorder ──────────────────────────────────

function QuickRankView({
  proposals,
  onFinalize,
  finalizing,
  refusalLine = null,
}: {
  proposals: CatchballProposal[];
  /** Null when the person may not accept these proposals (D-4); `refusalLine` says why. */
  onFinalize: ((ranking: RankedEntry[]) => void) | null;
  finalizing: boolean;
  refusalLine?: string | null;
}) {
  const allItemTitles = useMemo(() => buildItemPool(proposals), [proposals]);

  const [ranking, setRanking] = useState<RankedEntry[]>(() =>
    allItemTitles.map((item, i) => ({
      rank: i + 1,
      title: item.title,
      rationale: '',
      sourceOrgUnit: item.sources.join(', '),
      proposalId: item.proposalId,
      accepted: true,
    }))
  );
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const moveItem = (from: number, to: number) => {
    setRanking((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next.map((entry, i) => ({ ...entry, rank: i + 1 }));
    });
  };

  const handleDragOver = (e: React.DragEvent, i: number) => { e.preventDefault(); setOverIndex(i); };
  const handleDrop = (i: number) => {
    if (dragIndex !== null && dragIndex !== i) moveItem(dragIndex, i);
    setDragIndex(null);
    setOverIndex(null);
  };

  const updateRationale = (index: number, rationale: string) =>
    setRanking((prev) => prev.map((e, i) => (i === index ? { ...e, rationale } : e)));
  const toggleAccepted = (index: number) =>
    setRanking((prev) => prev.map((e, i) => (i === index ? { ...e, accepted: !e.accepted } : e)));

  const accepted = ranking.filter((e) => e.accepted);

  return (
    <div className="space-y-4">
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden">
        <div className="px-5 py-3.5 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg)] flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Executive ranking</h3>
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {accepted.length} accepted · {ranking.length - accepted.length} excluded
          </span>
        </div>
        <ol className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {ranking.map((entry, index) => (
            <li
              key={`${entry.proposalId}-${index}`}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={() => handleDrop(index)}
              onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
              className={`flex items-start gap-3 px-4 py-3.5 transition-colors cursor-grab ${
                overIndex === index
                  ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30'
                  : entry.accepted
                  ? 'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]'
                  : 'bg-[var(--surface)] dark:bg-[var(--bg)] opacity-50'
              }`}
            >
              <GripVertical className="mt-1 h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-dim)] shrink-0" />
              <button type="button" onClick={() => toggleAccepted(index)} className="mt-1 shrink-0">
                {entry.accepted
                  ? <CheckCircle2 className="h-5 w-5 text-[var(--green)]" />
                  : <div className="h-5 w-5 rounded-full border-2 border-[var(--rule)] dark:border-[var(--rule)]" />}
              </button>
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-sm font-mono text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-0.5 w-6 shrink-0">{index + 1}</span>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${entry.accepted ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}`}>
                      {entry.title}
                    </p>
                    <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                      Returned by: {entry.sourceOrgUnit}
                    </p>
                  </div>
                </div>
                {entry.accepted && (
                  <div className="ml-8">
                    <input
                      value={entry.rationale}
                      onChange={(e) => updateRationale(index, e.target.value)}
                      placeholder="Executive rationale (optional but recommended)…"
                      className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg)] px-3 py-1.5 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] focus:border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    />
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {accepted.length === 0 && (
        <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 dark:border-[var(--mustard)] px-4 py-3 flex items-center gap-2 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
          <AlertCircle className="h-4 w-4 shrink-0" />
          At least one item must be accepted before it can go into the cycle report.
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          {accepted.length} priorities will be accepted into the cycle report.
        </p>
        {onFinalize ? (
          <button
            type="button"
            onClick={() => onFinalize(accepted)}
            disabled={finalizing || accepted.length === 0}
            className="inline-flex items-center gap-2 bg-[var(--slate)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--slate)] disabled:opacity-60"
          >
            {finalizing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trophy className="h-4 w-4" />}
            {finalizing ? 'Accepting…' : 'Accept into the cycle report'}
          </button>
        ) : refusalLine ? (
          <p className="max-w-sm text-right text-sm text-[var(--text-ter)]">{refusalLine}</p>
        ) : null}
      </div>
    </div>
  );
}

// ─── Sub-view B: Launch a formal reconciliation session ───────────────────────

function LaunchSessionView({
  proposals,
  cycleId,
  trackId,
}: {
  proposals: CatchballProposal[];
  cycleId: string;
  trackId?: string;
}) {
  const navigate = useNavigate();
  const currentUserId = useUserStore((s) => s.userId);

  const itemPool = useMemo(() => buildItemPool(proposals), [proposals]);

  const defaultName = 'Executive Reconciliation Session';
  const [sessionName, setSessionName] = useState(defaultName);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!currentUserId) { setError('No authenticated user found.'); return; }
    setCreating(true);
    setError(null);
    try {
      const result = await createSessionWithFeedback({
        name: sessionName.trim() || defaultName,
        description: 'Executive reconciliation — all upward proposals pre-loaded.',
        cycleId,
        trackId: trackId ?? null,
        sessionContextMode: trackId ? 'cycle_linked' : 'isolated',
        facilitatorId: currentUserId,
        createdBy: currentUserId,
        intent: 'rank_priorities',
        // Pre-load item titles from proposals as isolated items.
        // These titles may be promoted to linked workspace items in the Items tab.
        isolatedItems: itemPool.map((it) => it.title),
      });
      if (!result.ok || !result.sessionId) {
        setError(result.error ?? 'Session creation failed. Please try again.');
        setCreating(false);
        return;
      }
      // Land on the Configure group — Participants tab first
      navigate(`/prioritization/sessions/${result.sessionId}?tab=participants`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setCreating(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Explainer */}
      <div className="border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 px-5 py-4">
        <div className="flex items-start gap-3">
          <Play className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-[var(--text-sec)]">
              Run a formal reconciliation session
            </p>
            <p className="mt-1 text-xs text-[var(--text-sec)] leading-relaxed">
              This creates a new session pre-loaded with all proposed items from this Catchball Loop.
              You'll still configure who participates, which criteria to use, and which tool to run —
              then go through Setup → Run → Results. Produces the strongest decision lineage.
            </p>
          </div>
        </div>
        {/* Executive-only visibility note */}
        <div className="mt-3 flex items-center gap-1.5 text-xs text-[var(--text-sec)]">
          <Lock className="h-3 w-3 shrink-0" />
          Visible only to users at or above the executive org unit — not shown to division-level participants.
        </div>
      </div>

      {/* Pre-loaded items preview */}
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--bg)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListOrdered className="h-4 w-4 text-[var(--text-ter)]" />
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Items pre-loaded from proposals
            </span>
          </div>
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">{itemPool.length} items</span>
        </div>
        <ul className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)] max-h-64 overflow-y-auto">
          {itemPool.map((item, i) => (
            <li key={i} className="flex items-start gap-3 px-5 py-2.5">
              <span className="text-xs font-mono text-[var(--text-ter)] dark:text-[var(--text-dim)] mt-0.5 w-5 shrink-0 text-right">{i + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">{item.title}</p>
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  {item.sources.join(' · ')}
                </p>
              </div>
            </li>
          ))}
        </ul>
        {itemPool.length === 0 && (
          <div className="px-5 py-6 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] text-center">
            No proposed items found in returned proposals.
          </div>
        )}
      </div>

      {/* Still to configure */}
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3">
          You'll configure after creation
        </p>
        <ul className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          {['Participants (who votes)', 'Criteria & weights', 'Values / ranges', 'Session tool', 'Setup & run'].map((step) => (
            <li key={step} className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--slate)] shrink-0" />
              {step}
            </li>
          ))}
        </ul>
      </div>

      {/* Session name + create */}
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-1.5">
            Session name
          </label>
          <input
            value={sessionName}
            onChange={(e) => setSessionName(e.target.value)}
            className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] px-3 py-2 focus:border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900"
          />
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={creating || itemPool.length === 0}
          className="inline-flex items-center gap-2 bg-[var(--slate)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--slate)] disabled:opacity-60 whitespace-nowrap"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          {creating ? 'Creating…' : 'Create reconciliation session'}
        </button>
      </div>
      {error && (
        <p className="text-sm text-[var(--coral)] dark:text-[var(--coral)]">{error}</p>
      )}
    </div>
  );
}

// ─── Mode 3 container: choose a path ─────────────────────────────────────────

function ExecutiveSessionView({
  proposals,
  onFinalize,
  finalizing,
  cycleId,
  trackId,
  refusalLine = null,
}: {
  proposals: CatchballProposal[];
  onFinalize: ((ranking: RankedEntry[]) => void) | null;
  finalizing: boolean;
  cycleId: string;
  trackId?: string;
  refusalLine?: string | null;
}) {
  type Path = 'reorder' | 'session';
  const [path, setPath] = useState<Path | null>(null);

  if (path === null) {
    return (
      <div className="space-y-4">
        <div className="border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)]/60 dark:bg-[var(--slate)]/20 px-5 py-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            <Users className="h-4 w-4" />
            Executive Reconciliation — choose your path
          </div>
          <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            You can do a quick executive reorder right here, or kick off a formal prioritization session
            with the full tool registry — whichever produces the decision lineage you need.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Path A */}
          <button
            type="button"
            onClick={() => setPath('reorder')}
            className="text-left border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-5 hover:border-[var(--slate)] dark:hover:border-[var(--slate)] hover:shadow-sm transition-all group"
          >
            <div className="flex items-center gap-2 mb-2">
              <ListOrdered className="h-5 w-5 text-[var(--text-ter)] group-hover:text-[var(--text-ter)]" />
              <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Quick reorder</span>
            </div>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-relaxed">
              Drag items into executive priority order, check/uncheck to include or exclude,
              and add rationale. Accept directly into the cycle report.
            </p>
            <p className="mt-3 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] group-hover:underline">
              Start reordering →
            </p>
          </button>

          {/* Path B */}
          <button
            type="button"
            onClick={() => setPath('session')}
            className="text-left border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-5 hover:border-[var(--slate)] dark:hover:border-[var(--slate)] hover:shadow-sm transition-all group"
          >
            <div className="flex items-center gap-2 mb-2">
              <Play className="h-5 w-5 text-[var(--text-ter)] group-hover:text-[var(--text-ter)]" />
              <span className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Run a session</span>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 px-1.5 py-0.5 rounded">
                <Lock className="h-2.5 w-2.5" />
                Executive only
              </span>
            </div>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] leading-relaxed">
              Configure participants, criteria, and a ranking tool. Run a formal session
              with full decision lineage. Items from all returned proposals are pre-loaded.
            </p>
            <p className="mt-3 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] group-hover:underline">
              Set up session →
            </p>
          </button>
        </div>
      </div>
    );
  }

  if (path === 'reorder') {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPath(null)}
            className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
          >
            ← Back
          </button>
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">·</span>
          <span className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">Quick reorder</span>
        </div>
        <QuickRankView proposals={proposals} onFinalize={onFinalize} finalizing={finalizing} refusalLine={refusalLine} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setPath(null)}
          className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
        >
          ← Back
        </button>
        <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">·</span>
        <span className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">Run a session</span>
      </div>
      <LaunchSessionView proposals={proposals} cycleId={cycleId} trackId={trackId} />
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function ExecutiveReconciliationPage() {
  const { cycleId } = useParams<{ cycleId: string }>();

  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const proposals = useProposalPacketStore((s) => s.proposals);
  const fetchProposals = useProposalPacketStore((s) => s.fetchProposals);
  const acceptProvisional = useProposalPacketStore((s) => s.acceptProvisional);
  const isLoading = useProposalPacketStore((s) => s.isLoading);
  const userId = useUserStore((s) => s.userId);

  const [mode, setMode] = useState<ReconciliationMode>('accept');
  const [accepting, setAccepting] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [finalized, setFinalized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modeInitialized, setModeInitialized] = useState(false);

  useEffect(() => {
    if (cycleId) {
      fetchLoops(cycleId);
      fetchProposals(cycleId);
    }
  }, [cycleId, fetchLoops, fetchProposals]);

  // Filter: upward packets only, statuses that must appear in demo (incl. FY2027 accepted_provisional)
  const cycleProposals = useMemo(
    () =>
      proposals.filter(
        (p) =>
          p.cycleId === cycleId &&
          p.direction === 'up' &&
          isReconciliationVisibleStatus(p.status)
      ),
    [proposals, cycleId]
  );

  const proposalsNeedingAccept = useMemo(
    () => cycleProposals.filter((p) => isProposalAwaitingDecision(p.status)),
    [cycleProposals]
  );

  // D-4 (migration 987): finalizing accepts every waiting proposal, so it is
  // drawn only when the person may accept every one; otherwise the first
  // refusal's plain line is shown in its place (build contract §2.8).
  const proposalIdsNeedingAccept = useMemo(() => proposalsNeedingAccept.map((p) => p.id), [proposalsNeedingAccept]);
  const finalizeRight = useCatchballProvisionalAcceptRights(proposalIdsNeedingAccept);
  const mayFinalize = proposalIdsNeedingAccept.length === 0 || finalizeRight.allowedProvisional;

  const acceptedCount = useMemo(
    () => cycleProposals.filter((p) => p.status === 'accepted').length,
    [cycleProposals]
  );

  const provisionalCount = useMemo(
    () => cycleProposals.filter((p) => p.status === 'accepted_provisional').length,
    [cycleProposals]
  );

  /** Mode 1 (and post-accept landing): nothing left to accept — next step is the cycle report / PoR. */
  const mode1Complete =
    cycleProposals.length > 0 && proposalsNeedingAccept.length === 0;

  const provisionalOnly = acceptedCount === 0 && provisionalCount > 0;

  // Prefer Mode 1 when work is already done so the completion CTA is visible on arrival.
  useEffect(() => {
    if (modeInitialized || isLoading || cycleProposals.length === 0) return;
    if (mode1Complete) setMode('accept');
    setModeInitialized(true);
  }, [modeInitialized, isLoading, cycleProposals.length, mode1Complete]);

  const handleAcceptOne = async (proposalId: string) => {
    if (!userId) return;
    setAccepting(proposalId);
    setError(null);
    try {
      await acceptProvisional({ proposalId, responseType: 'accept_provisional', actorId: userId });
      await fetchProposals(cycleId!);
      const remaining = useProposalPacketStore
        .getState()
        .proposals.filter(
          (p) =>
            p.cycleId === cycleId &&
            p.direction === 'up' &&
            isReconciliationVisibleStatus(p.status) &&
            (p.status === 'pending' || p.status === 'acknowledged')
        );
      if (remaining.length === 0) setFinalized(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Accept failed');
    } finally {
      setAccepting(null);
    }
  };

  const handleFinalize = async (_ranking: RankedEntry[]) => {
    if (!userId) return;
    setFinalizing(true);
    setError(null);
    try {
      // Accept all pending proposals
      const pending = proposalsNeedingAccept;
      await Promise.all(
        pending.map((p) =>
          acceptProvisional({ proposalId: p.id, responseType: 'accept_provisional', actorId: userId })
        )
      );
      setFinalized(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Finalization failed');
    } finally {
      setFinalizing(false);
    }
  };

  // ── Finalized success state ────────────────────────────────────────────────
  if (finalized) {
    return (
      <div className="min-h-full bg-[var(--surface)] dark:bg-[var(--bg)]">
        <div className="mx-auto max-w-xl px-4 py-16 text-center">
          <div className="flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--green-dim)]">
              <Trophy className="h-8 w-8 text-[var(--green)]" />
            </div>
          </div>
          <h2 className="mt-6 text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Executive reconciliation complete
          </h2>
          <p className="mt-3 text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            Team rankings are accepted. Open the cycle report — that is this cycle&apos;s
            ratified priorities.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <Link
              to={`/prioritization/cycles/${cycleId}/report`}
              className="inline-flex items-center gap-2 bg-[var(--slate)] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[var(--slate)]"
            >
              <ArrowRight className="h-4 w-4" />
              Open Published Priority List
            </Link>
            <Link
              to={`/prioritization/cascade/dashboard/${cycleId}`}
              className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]"
            >
              Back to cycle dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[var(--surface)] dark:bg-[var(--bg)]">
      {cycleId && <CatchballStickyHeader cycleId={cycleId} active="reconcile" />}
      <div className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="space-y-6">

          {/* Header */}
          <div>
            <h1 className="mt-2 text-2xl font-semibold text-[var(--text-pri)]">Executive Reconciliation</h1>
            <p className="mt-1 text-sm text-[var(--text-ter)]">
              Each card is an upward Proposal returned on a different Ball in the same Catchball Loop.
              Choose how to produce the final accepted ranking.
            </p>
          </div>

          {/* Mode selector */}
          <div className="grid gap-3 sm:grid-cols-3">
            {(Object.entries(MODE_META) as [ReconciliationMode, typeof MODE_META[ReconciliationMode]][]).map(
              ([id, meta]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMode(id)}
                  className={` border p-4 text-left transition-colors ${
                    mode === id
                      ? 'border-[var(--slate)] bg-[var(--slate)]/10 ring-1 ring-indigo-500'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-[var(--slate)] hover:bg-[var(--surface)]'
                  }`}
                >
                  <div className={`flex items-center gap-2 font-semibold text-sm ${mode === id ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}>
                    {meta.icon}
                    {meta.label}
                  </div>
                  <p className="mt-1.5 text-xs text-[var(--text-ter)] leading-relaxed">{meta.description}</p>
                </button>
              )
            )}
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading upward proposals…
            </div>
          )}

          {!isLoading && cycleProposals.length === 0 && (
            <div className="border border-dashed border-[var(--rule)] bg-[var(--bg-raised)] p-10 text-center">
              <Users className="mx-auto h-10 w-10 text-[var(--text-ter)]" />
              <p className="mt-3 text-sm font-medium text-[var(--text-sec)]">No upward proposals yet</p>
              <p className="mt-1 text-xs text-[var(--text-ter)]">
                Downstream groups need to return Proposals on their Balls before reconciliation can begin.
              </p>
            </div>
          )}

          {error && (
            <div className="border border-[var(--coral)] bg-[var(--coral-dim)] px-4 py-3 flex items-center gap-2 text-sm text-[var(--coral)]">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {!isLoading && cycleProposals.length > 0 && (
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
              <div className="space-y-6">

                {/* Mode 1: Accept directly */}
                {mode === 'accept' && (
                  <div className="space-y-4">
                    {mode1Complete ? (
                      <div className="border border-[var(--green)] dark:border-[var(--green)]/60 bg-[var(--green-dim)] dark:bg-[var(--green)]/25 p-5 space-y-4">
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="h-6 w-6 text-[var(--green)] dark:text-[var(--text-ter)] shrink-0 mt-0.5" />
                          <div>
                            <h2 className="text-base font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
                              Mode 1 complete — rankings accepted
                            </h2>
                            <p className="mt-1 text-sm text-[var(--green)]/90 dark:text-[var(--text-ter)]/90">
                              There is nothing left to accept on this page. Next step: open the
                              Published Priority List (cycle report). That ranking is the Plan of
                              Record output for this Catchball loop.
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Link
                            to={`/prioritization/cycles/${cycleId}/report`}
                            className="inline-flex items-center gap-2 bg-[var(--slate)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--slate)]"
                          >
                            <Trophy className="h-4 w-4" />
                            Open Published Priority List
                          </Link>
                          <Link
                            to={`/prioritization/cascade/dashboard/${cycleId}`}
                            className="inline-flex items-center gap-2 border border-[var(--green)] dark:border-[var(--green)] bg-[var(--bg-raised)]/80 dark:bg-[var(--surface)] px-4 py-2.5 text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]"
                          >
                            Back to Catchball dashboard
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                        Accept each upward Proposal as submitted from each Ball. Their ranked list stands
                        as that Ball&apos;s returned input. When every Ball is accepted, you&apos;ll get a
                        clear link to the Published Priority List.
                      </p>
                    )}
                    {cycleProposals.map((p) => (
                      <ProposalCard
                        key={p.id}
                        proposal={p}
                        mode={mode}
                        onAccept={handleAcceptOne}
                        accepting={accepting === p.id}
                      />
                    ))}
                  </div>
                )}

                {/* Mode 2: Aggregate */}
                {mode === 'aggregate' && (
                  <AggregateView
                    proposals={cycleProposals}
                    onConfirm={mayFinalize ? handleFinalize : null}
                    confirming={finalizing}
                    refusalLine={finalizeRight.line}
                  />
                )}

                {/* Mode 3: Executive session */}
                {mode === 'session' && (
                  <ExecutiveSessionView
                    proposals={cycleProposals}
                    onFinalize={mayFinalize ? handleFinalize : null}
                    finalizing={finalizing}
                    cycleId={cycleId ?? ''}
                    refusalLine={finalizeRight.line}
                  />
                )}
              </div>

              {/* Sidebar — Balls / upward proposals */}
              <aside className="space-y-4">
                <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
                  <h3 className="text-sm font-semibold text-[var(--text-pri)] mb-3">
                    Returned proposals (same Catchball Loop)
                  </h3>
                  <div className="space-y-3">
                    {cycleProposals.map((p) => (
                      <div key={p.id} className="flex flex-col gap-1 text-sm border-b border-[var(--rule)] last:border-0 pb-3 last:pb-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[var(--text-pri)] font-medium">{sidebarBallLabel(p)}</span>
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${statusBadgeClass(p.status)}`}
                          >
                            {formatReconciliationProposalStatus(p.status)}
                          </span>
                        </div>
                        <span className="text-xs text-[var(--text-ter)]">
                          {countReturnedPriorities(p)} proposed priorities · Updated{' '}
                          {new Date(p.updatedAt ?? p.proposedAt).toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {(acceptedCount > 0 || provisionalOnly) && (
                  <div className="border border-[var(--green)] dark:border-[var(--green)]/50 bg-[var(--green-dim)] dark:bg-[var(--green)]/25 p-5">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
                      <CheckCircle2 className="h-4 w-4" />
                      {mode1Complete ? 'Next step: Published Priority List' : 'Ready for the cycle report'}
                    </div>
                    <p className="mt-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]/90">
                      {acceptedCount > 0 ? (
                        <>
                          {acceptedCount} proposal{acceptedCount !== 1 ? 's' : ''} accepted.
                          The cycle report holds the ratified priorities for this walkthrough.
                        </>
                      ) : (
                        <>
                          {provisionalCount} upward proposal{provisionalCount !== 1 ? 's' : ''}{' '}
                          accepted provisionally. Open the cycle report, or use Mode 2 / 3 if you
                          still want to reshape the ranking.
                        </>
                      )}
                    </p>
                    <Link
                      to={`/prioritization/cycles/${cycleId}/report`}
                      className="mt-3 inline-flex w-full items-center justify-center gap-1.5 bg-[var(--slate)] px-3 py-2.5 text-sm font-semibold text-white hover:bg-[var(--slate)]"
                    >
                      Open Published Priority List
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                )}
              </aside>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExecutiveReconciliationPage;
